import { useEffect, useRef, useState } from 'react';
import { CaptionsCue, PlayerBar } from '@praktio/common-ui';
import ReactPlayer from 'react-player';
import { CSSTransition } from 'react-transition-group';
import { Utils } from '@praktio/common';
import { ICaptionsData } from '@praktio/common/lib/typings';
import classNames from 'classnames';

import './Player.scss';

const { parseVTT } = Utils.Captions;
interface IPlayerState {
  played: number;
  playedSeconds: number;
  loaded: number;
  loadedSeconds: number;
}

const PlayerBarHideInterval = 1000 * 2; // Two seconds;

// TODO: Potential candidate for Common UI (used in Player and Explainer)
const FlashIcon = ({ icon, show, hide }: { icon: string; show?: boolean; hide: () => void }) => {
  let flashIcon = '';
  switch (icon) {
    case 'play':
      flashIcon = 'play_circle';
      break;
    case 'pause':
      flashIcon = 'pause_circle';
      break;
    case 'rewind':
      flashIcon = 'replay_10';
      break;
  }

  return (
    <CSSTransition
      in={show}
      timeout={300}
      classNames="FlashIcon-animation"
      unmountOnExit={true}
      onEntered={() => {
        hide();
      }}
    >
      <span className="material-icons FlashIcon">{flashIcon}</span>
    </CSSTransition>
  );
};

const Player = ({
  data,
  type,
  disableAutoPlay,
  unlocked,
  onPlayerEnded,
  className,
  ignoreKeyPress,
}: {
  data?: IVideo | File;
  type: 'VIDEO' | 'AUDIO';
  disableAutoPlay?: boolean;
  unlocked?: boolean;
  onPlayerEnded?: () => void;
  className?: string;
  ignoreKeyPress?: boolean;
}) => {
  const playerRef = useRef(null);
  const [url, setUrl] = useState<string | undefined>();
  useEffect(() => {
    if (data && !(data as any).url) {
      setUrl(URL.createObjectURL(data as any));
    } else {
      setUrl((data as any)?.url);
    }
  }, [data]);

  const captions = (data as IVideo)?.captions;

  const playerBarRef = useRef<HTMLDivElement>(null);
  const [loaded, setLoaded] = useState(false);
  const [showPlayerBar, setShowPlayerBar] = useState(false);
  const [flashIcon, setFlashIcon] = useState('');
  const [showFlashIcon, setShowFlashIcon] = useState(false);
  const timerId = useRef<NodeJS.Timeout>();
  const [duration, setDuration] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [playerState, setPlayerState] = useState<IPlayerState>();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [seeking, setSeeking] = useState(false);
  const [playerBarVisible, setPlayerBarVisible] = useState(false);
  const [autoAdvance, setAutoAdvance] = useState(localStorage.getItem('autoAdvance') === 'true');
  const [showCaptions, setShowCaptions] = useState(localStorage.getItem('showCaptions') === 'true');
  const initialPlaybackRate: string | null = localStorage.getItem('playbackRate');
  const [playbackRate, setPlaybackRate] = useState(
    initialPlaybackRate && (unlocked || parseFloat(initialPlaybackRate) <= 1)
      ? parseFloat(initialPlaybackRate)
      : 1,
  );
  const [processedCaptions, setProcessedCaptions] = useState<ICaptionsData[]>(parseVTT(captions));

  /* Start PlayerBar show/hide logic */
  const showPlayerAndResetTimer = () => {
    setShowPlayerBar(true);
    if (timerId.current) {
      clearTimeout(timerId.current);
    }
    timerId.current = setTimeout(() => setShowPlayerBar(false), PlayerBarHideInterval);
  };

  useEffect(() => {
    setShowPlayerBar(true);
    setPlayerBarVisible(true);
  }, []);

  const handleMouseMove = () => {
    showPlayerAndResetTimer();
  };

  const handleWindowKeyDown = (event: KeyboardEvent) => {
    if (!ignoreKeyPress) {
      showPlayerAndResetTimer();
      if (event.code === 'Space') {
        event.stopPropagation();
        event.preventDefault();
        setPlaying(!playing);
        setFlashIcon(playing ? 'pause' : 'play');
        setShowFlashIcon(true);
      }
    }
  };

  const handlePlayerBarKeyDown = (event: KeyboardEvent) => {
    showPlayerAndResetTimer();
    if (!ignoreKeyPress && event.code === 'Space') {
      event.stopPropagation();
    }
  };

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('keydown', handleWindowKeyDown);

    const pbRef = playerBarRef.current;
    if (pbRef) {
      pbRef.addEventListener('keydown', handlePlayerBarKeyDown);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('keydown', handleWindowKeyDown);

      if (pbRef) {
        pbRef.removeEventListener('keydown', handlePlayerBarKeyDown);
      }
    };
  });

  /* End PlayerBar show/hide logic */

  const onPlayerProgress = (state: IPlayerState) => {
    setPlayerState(state);
  };

  const onPlaying = (value: boolean) => {
    setPlaying(value);
    setFlashIcon(!value ? 'pause' : 'play');
    setShowFlashIcon(true);
  };

  const onSeeking = (value: boolean) => {
    setPlaying(!value);
  };

  const onSeekTo = (value: number, isReplay?: boolean) => {
    if (playerRef.current) {
      (playerRef.current as any).seekTo(value);
    }

    if (isReplay) {
      setFlashIcon('rewind');
      setShowFlashIcon(true);
    }
  };

  const updateAutoAdvance = (value: boolean) => {
    localStorage.setItem('autoAdvance', value.toString());
    setAutoAdvance(value);
  };

  const updateShowCaptions = () => {
    const value = !showCaptions;
    localStorage.setItem('showCaptions', value.toString());
    setShowCaptions(value);
  };

  const updatePlaybackRate = (value: number) => {
    let rate = value;
    if (!unlocked && playbackRate > 1) {
      rate = 1;
    }

    localStorage.setItem('playbackRate', rate.toString());
    setPlaybackRate(rate);
  };

  useEffect(() => {
    if (captions) {
      setProcessedCaptions(parseVTT(captions));
    }
  }, [captions]);

  const config = {
    file: {
      attributes: {
        crossOrigin: 'true',
        controls: false,
        autoPlay: disableAutoPlay ? false : true,
      },
    },
  };

  let currentCaptions: string[] = [];
  if (showCaptions && processedCaptions && playerState) {
    // Work backwards to find the captions for the current time
    for (let i = processedCaptions.length - 1; i >= 0; i -= 1) {
      const data = processedCaptions[i];
      // Multiply by a 1000 because the data is in milliseconds
      if (playerState.playedSeconds * 1000 >= (data.start || 0)) {
        currentCaptions = data.captions;
        break;
      }
    }
  }

  return (
    <div className={classNames('Player', className)}>
      <FlashIcon icon={flashIcon} show={showFlashIcon} hide={() => setShowFlashIcon(false)} />
      <ReactPlayer
        className="Player-react-player"
        ref={playerRef}
        url={url}
        config={config}
        playing={loaded ? !seeking && playing : false}
        playbackRate={loaded ? playbackRate : 1}
        volume={loaded ? 1 : undefined}
        progressInterval={25}
        onReady={() => setLoaded(true)}
        onPlay={() => {
          if (loaded) {
            setPlaying(true);
            setSeeking(false);
          }
        }}
        onProgress={onPlayerProgress}
        onEnded={() => {
          if (timerId.current) {
            clearTimeout(timerId.current);
          }
          setShowPlayerBar(true);
          if (onPlayerEnded) {
            onPlayerEnded();
          }
        }}
        onError={(err: Error) => console.error(err)}
        onDuration={(value: number) => setDuration(value)}
      />
      <CSSTransition
        in={showPlayerBar || !playing || settingsOpen}
        timeout={300}
        classNames="PlayerBar-animation"
        // These pieces of state are 'self-owned' by the player bar
        // in order to determine when it can display tooltips.
        onEntered={() => setPlayerBarVisible(true)}
        onExited={() => setPlayerBarVisible(false)}
      >
        <PlayerBar
          playing={playing}
          unlocked={unlocked || false}
          duration={duration}
          playedSeconds={playerState?.playedSeconds || 0}
          playbackRate={playbackRate}
          autoAdvance={autoAdvance}
          showCaptions={showCaptions}
          onSeeking={onSeeking}
          onSeekTo={onSeekTo}
          onPlaying={onPlaying}
          updatePlaybackRate={updatePlaybackRate}
          updateAutoAdvance={updateAutoAdvance}
          updateShowCaptions={updateShowCaptions}
          setSettingsOpen={setSettingsOpen}
          showPlayerAndResetTimer={showPlayerAndResetTimer}
          autoHideable
          isBeingShown={playerBarVisible}
          ref={playerBarRef}
        />
      </CSSTransition>
      <CaptionsCue
        playerBarActive={showPlayerBar || !playing || settingsOpen}
        lines={currentCaptions}
      />
    </div>
  );
};

export default Player;
